import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "ab_session_id";

const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
};

// Envia um evento ao PostgREST de forma resiliente a unload/navegação.
// Em produção, sendBeacon cross-origin pode ser bloqueado por CORS aqui,
// então priorizamos fetch keepalive e caímos para supabase-js se necessário.
const sendEvent = (payload: Record<string, unknown>) => {
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/ab_events`;
    const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
    const body = JSON.stringify(payload);

    void fetch(url, {
      method: "POST",
      keepalive: true,
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        apikey,
        Authorization: `Bearer ${apikey}`,
        Prefer: "return=minimal",
      },
      body,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await response.text());
        }
      })
      .catch(() => {
        // Último fallback via supabase-js
        supabase.from("ab_events").insert(payload as never).then(() => {});
      });
  } catch {
    try {
      supabase.from("ab_events").insert(payload as never).then(() => {});
    } catch {
      // ignore
    }
  }
};

// Dedupe view per session
const viewedKey = (variant: string) => `ab_viewed_${variant}`;

export const recordAbView = async (variant: string) => {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(viewedKey(variant))) return;
    sessionStorage.setItem(viewedKey(variant), "1");
  } catch {
    // ignore
  }
  sendEvent({
    event_type: "view",
    variant,
    session_id: getSessionId(),
  });
};

export const recordAbClick = async (
  variant: string,
  ctaLabel?: string,
  eventType: "click" | "checkout_click" = "click",
) => {
  if (typeof window === "undefined") return;
  // Usa sendBeacon para sobreviver à navegação (window.open p/ checkout)
  sendEvent({
    event_type: eventType,
    variant,
    cta_label: ctaLabel ?? null,
    session_id: getSessionId(),
  });
};

// ---- Time on page tracking ----
const TIME_SENT_KEY = "ab_time_sent";

export const startTimeTracking = (variant: string) => {
  if (typeof window === "undefined") return;

  const startedAt = Date.now();

  const send = () => {
    try {
      if (sessionStorage.getItem(TIME_SENT_KEY)) return;
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      // Ignora durações absurdas (aba em background por horas)
      if (seconds < 1 || seconds > 60 * 60 * 4) return;
      sessionStorage.setItem(TIME_SENT_KEY, "1");

      sendEvent({
        event_type: "time_on_page",
        variant,
        session_id: getSessionId(),
        duration_seconds: seconds,
      });
    } catch {
      // ignore
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === "hidden") send();
  };

  window.addEventListener("pagehide", send);
  window.addEventListener("beforeunload", send);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.removeEventListener("pagehide", send);
    window.removeEventListener("beforeunload", send);
    document.removeEventListener("visibilitychange", onVisibility);
  };
};
